import { useEffect, useMemo, useState, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { UserPlus, Trash2, Pencil, CheckCircle2, XCircle, MailPlus, Send, LogIn, PanelLeft, ChevronDown } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { fmtDate } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { SuppliersSection } from "../components/SuppliersSection";

const ROLE_TR = { admin: "Yönetici", user: "Firma Kullanıcısı", supplier: "Tedarikçi", malkabul: "Mal Kabul", satinalma: "Satınalma", izleyici: "İzleyici" };
const MENUS = [
  ["dashboard", "Genel Bakış"],
  ["orders", "Tüm Siparişler"],
  ["reports", "Rapor"],
  ["finance", "Finansal Rapor (yönetici)"],
  ["goods_receipt", "Mal Kabul"],
  ["settings", "Ayarlar (yönetici)"],
  ["catalog", "Ürün Kataloğu"],
  ["reconciliation", "Uyum Raporu"],
  ["users", "Kullanıcılar (yönetici)"],
  ["supplier_orders", "Siparişlerim (tedarikçi)"],
];
const EMPTY = { name: "", email: "", password: "", role: "user", organisation: "", supplier_id: "" };
const EMPTY_INVITE = { email: "", role: "user", organisation: "", supplier_id: "" };

const RolePicker = ({ value, onChange, testId }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="rounded-sm border-stone-300" data-testid={testId}>
      <SelectValue />
    </SelectTrigger>
    <SelectContent>
      <SelectItem value="user">Firma Kullanıcısı</SelectItem>
      <SelectItem value="admin">Yönetici</SelectItem>
      <SelectItem value="supplier">Tedarikçi</SelectItem>
      <SelectItem value="satinalma">Satınalma</SelectItem>
      <SelectItem value="malkabul">Mal Kabul</SelectItem>
      <SelectItem value="izleyici">İzleyici</SelectItem>
    </SelectContent>
  </Select>
);

const SupplierPicker = ({ suppliers, value, onChange, testId }) => (
  <div className="space-y-1.5">
    <Label className="text-xs uppercase tracking-wider text-stone-600">Atanacak Tedarikçi</Label>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="rounded-sm border-stone-300" data-testid={testId}>
        <SelectValue placeholder="Tedarikçi seçin" />
      </SelectTrigger>
      <SelectContent className="max-h-64">
        {suppliers.map((s) => (
          <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
        ))}
      </SelectContent>
    </Select>
    {suppliers.length === 0 && <p className="text-[11px] text-amber-700">Önce aşağıdaki "Tedarikçiler" bölümünden tedarikçi ekleyin.</p>}
  </div>
);

export default function UsersPage() {
  const { user: me, impersonate } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [approving, setApproving] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [invites, setInvites] = useState([]);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState(EMPTY_INVITE);
  const [inviting, setInviting] = useState(false);
  const [suppliers, setSuppliers] = useState([]);

  const load = () => api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
  const loadInvites = () => api.get("/invites").then((r) => setInvites(r.data)).catch(() => {});

  const enterAccount = async (u) => {
    try {
      await impersonate(u.id);
      toast.success(`${u.name || u.email} hesabına geçildi`);
      navigate("/");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const [menuDialog, setMenuDialog] = useState(null);
  const [menuHidden, setMenuHidden] = useState([]);

  const ROLE_ORDER = ["admin", "user", "satinalma", "malkabul", "izleyici", "supplier"];
  const [openGroups, setOpenGroups] = useState({});
  const groupedUsers = useMemo(() => {
    const m = {};
    users.forEach((u) => {
      (m[u.role] = m[u.role] || []).push(u);
    });
    const ordered = ROLE_ORDER.filter((r) => m[r]).map((r) => [r, m[r]]);
    Object.keys(m).forEach((r) => {
      if (!ROLE_ORDER.includes(r)) ordered.push([r, m[r]]);
    });
    return ordered;
  }, [users]);
  const isGroupOpen = (role, list) => openGroups[role] ?? list.some((u) => u.status === "pending");

  const openMenuDialog = (u) => {
    setMenuDialog(u);
    setMenuHidden(u.hidden_menus || []);
  };

  const saveMenus = async () => {
    try {
      await api.post(`/users/${menuDialog.id}/menus`, { hidden: menuHidden });
      setUsers((list) => list.map((x) => (x.id === menuDialog.id ? { ...x, hidden_menus: menuHidden } : x)));
      toast.success("Menü görünürlüğü kaydedildi");
      setMenuDialog(null);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => {
    load();
    loadInvites();
    api.get("/filters").then((r) => setOrgs(r.data.organisations)).catch(() => {});
  }, []);

  const sendInvite = async () => {
    setInviting(true);
    try {
      await api.post("/invites", {
        ...inviteForm,
        organisation: inviteForm.role === "user" ? inviteForm.organisation : null,
        supplier_id: inviteForm.role === "supplier" ? inviteForm.supplier_id : null,
      });
      toast.success(`Davet e-postası ${inviteForm.email} adresine gönderildi`);
      setInviteOpen(false);
      setInviteForm(EMPTY_INVITE);
      loadInvites();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setInviting(false);
    }
  };

  const resendInvite = async (inv) => {
    try {
      await api.post(`/invites/${inv.id}/resend`);
      toast.success("Davet tekrar gönderildi (7 gün geçerli yeni bağlantı)");
      loadInvites();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const cancelInvite = async (inv) => {
    if (!window.confirm(`${inv.email} daveti iptal edilsin mi?`)) return;
    try {
      await api.delete(`/invites/${inv.id}`);
      toast.success("Davet iptal edildi");
      loadInvites();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const openCreate = () => {
    setEditing(null);
    setApproving(false);
    setForm(EMPTY);
    setOpen(true);
  };

  const openEdit = (u, approve = false) => {
    setEditing(u);
    setApproving(approve);
    setForm({ name: u.name || "", email: u.email, password: "", role: u.role, organisation: u.organisation || "", supplier_id: u.supplier_id || "" });
    setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) {
        const body = { name: form.name, role: form.role, organisation: form.role === "user" ? form.organisation : null, supplier_id: form.role === "supplier" ? form.supplier_id : null };
        if (form.password) body.password = form.password;
        if (approving) body.status = "approved";
        await api.put(`/users/${editing.id}`, body);
        toast.success(approving ? "Kullanıcı onaylandı ve bilgilendirme e-postası gönderildi" : "Kullanıcı güncellendi");
      } else {
        const r = await api.post("/users", { ...form, organisation: form.role === "user" ? form.organisation : null, supplier_id: form.role === "supplier" ? form.supplier_id : null });
        toast.success(r.data.welcomeEmailSent
          ? `Kullanıcı oluşturuldu, hoş geldin e-postası ${form.email} adresine gönderildi`
          : "Kullanıcı oluşturuldu (hoş geldin e-postası gönderilemedi)");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`${u.email} silinsin mi?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Kullanıcı silindi");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-4" data-testid="users-page">
      <div className="flex items-center justify-between border border-stone-200 bg-white p-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-stone-500">Kullanıcı Yönetimi</p>
          <p className="mt-1 text-sm text-stone-600">Kullanıcı oluşturun ve firmalara atayın. Firma kullanıcıları yalnızca kendi firmalarının siparişlerini görür.</p>
        </div>
        {me?.email === "mbucak@gmail.com" && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => { setInviteForm(EMPTY_INVITE); setInviteOpen(true); }} data-testid="invite-user-button" className="rounded-sm border-stone-300 hover:bg-stone-100 active:scale-95">
              <MailPlus size={15} className="mr-1.5" /> Davet Et
            </Button>
            <Button onClick={openCreate} data-testid="create-user-button" className="rounded-sm bg-[#1C4E35] text-white hover:bg-[#143A26] active:scale-95">
              <UserPlus size={15} className="mr-1.5" /> Yeni Kullanıcı
            </Button>
          </div>
        )}
      </div>

      {invites.length > 0 && (
        <div className="border border-stone-200 bg-white" data-testid="invites-section">
          <p className="border-b border-stone-200 px-4 py-3 text-xs font-semibold uppercase tracking-[0.2em] text-stone-500">
            Bekleyen Davetler ({invites.length})
          </p>
          <Table data-testid="invites-table">
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs">E-posta</TableHead>
                <TableHead className="text-xs">Rol</TableHead>
                <TableHead className="text-xs">Firma</TableHead>
                <TableHead className="text-xs">Geçerlilik</TableHead>
                <TableHead className="text-xs">Son Gönderim</TableHead>
                <TableHead className="text-right text-xs">İşlemler</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {invites.map((inv) => (
                <TableRow key={inv.id} className="hover:bg-stone-50" data-testid={`invite-row-${inv.email}`}>
                  <TableCell className="py-2 text-sm font-medium">{inv.email}</TableCell>
                  <TableCell className="py-2">
                    <Badge className={`rounded-sm text-[10px] ${inv.role === "admin" ? "bg-[#1C4E35] text-white hover:bg-[#1C4E35]" : "bg-stone-200 text-stone-700 hover:bg-stone-200"}`}>
                      {ROLE_TR[inv.role] || inv.role}
                    </Badge>
                  </TableCell>
                  <TableCell className="py-2 text-sm">{inv.organisation || inv.supplier_name || "—"}</TableCell>
                  <TableCell className="py-2 text-xs">
                    {inv.expired ? (
                      <span className="border border-red-300 bg-red-50 px-2 py-0.5 text-[10px] text-red-700">Süresi Doldu</span>
                    ) : (
                      <span className="text-stone-500">{fmtDate(inv.expires_at)} tarihine kadar</span>
                    )}
                  </TableCell>
                  <TableCell className="py-2 text-xs" data-testid={`invite-sent-${inv.email}`}>
                    {inv.last_sent_at ? (
                      <span className="flex flex-wrap items-center gap-1.5">
                        <span className="text-stone-600">{format(new Date(inv.last_sent_at), "dd.MM.yyyy HH:mm")}</span>
                        <span className="border border-green-400 bg-green-50 px-1.5 py-0.5 text-[10px] font-medium text-green-700">İletildi ✓</span>
                        {inv.send_count > 1 && <span className="text-[10px] text-stone-400">{inv.send_count}. gönderim</span>}
                      </span>
                    ) : (
                      <span className="text-stone-400">—</span>
                    )}
                  </TableCell>
                  <TableCell className="py-2 text-right">
                    <Button variant="ghost" size="sm" onClick={() => resendInvite(inv)} data-testid={`resend-invite-${inv.email}`} className="h-8 rounded-sm px-2 text-[#1C4E35] hover:bg-[#1C4E35]/10">
                      <Send size={14} className="mr-1" /> Tekrar Gönder
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => cancelInvite(inv)} data-testid={`cancel-invite-${inv.email}`} className="h-8 rounded-sm px-2 text-red-600 hover:bg-red-50">
                      <XCircle size={14} className="mr-1" /> İptal
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {me?.email === "mbucak@gmail.com" && <SuppliersSection onChanged={setSuppliers} />}

      <div className="border border-stone-200 bg-white">
        <Table data-testid="users-table">
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs">Kullanıcı</TableHead>
              <TableHead className="text-xs">Rol</TableHead>
              <TableHead className="text-xs">Atanan Firma</TableHead>
              <TableHead className="text-xs">Oluşturulma</TableHead>
              <TableHead className="text-right text-xs">İşlemler</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {groupedUsers.map(([role, list]) => (
              <Fragment key={role}>
                <TableRow
                  onClick={() => setOpenGroups((o) => ({ ...o, [role]: !isGroupOpen(role, list) }))}
                  className="cursor-pointer select-none bg-stone-50 hover:bg-stone-100"
                  data-testid={`user-group-${role}`}
                >
                  <TableCell colSpan={5} className="py-2.5">
                    <span className="flex items-center gap-2">
                      <ChevronDown size={14} className={`text-stone-400 transition-transform duration-200 ${isGroupOpen(role, list) ? "" : "-rotate-90"}`} />
                      <span className="font-heading text-xs font-black uppercase tracking-[0.15em] text-[#0A0D0B]">{ROLE_TR[role] || role}</span>
                      <span className="border border-stone-300 bg-white px-1.5 py-px text-[10px] font-medium text-stone-600">{list.length}</span>
                      {list.some((u) => u.status === "pending") && (
                        <span className="border border-amber-300 bg-amber-50 px-1.5 py-px text-[10px] font-medium text-amber-700">
                          {list.filter((u) => u.status === "pending").length} onay bekliyor
                        </span>
                      )}
                    </span>
                  </TableCell>
                </TableRow>
                {isGroupOpen(role, list) && list.map((u) => (
              <TableRow key={u.id} className="hover:bg-stone-50" data-testid={`user-row-${u.email}`}>
                <TableCell className="py-2">
                  <p className="text-sm font-medium">{u.name}</p>
                  <p className="text-[11px] text-stone-400">{u.email}</p>
                </TableCell>
                <TableCell className="py-2">
                  <Badge className={`rounded-sm text-[10px] ${u.role === "admin" ? "bg-[#1C4E35] text-white hover:bg-[#1C4E35]" : u.role === "supplier" ? "border border-[#1C4E35]/40 bg-[#1C4E35]/10 text-[#1C4E35] hover:bg-[#1C4E35]/10" : "bg-stone-200 text-stone-700 hover:bg-stone-200"}`}>
                    {ROLE_TR[u.role] || u.role}
                  </Badge>
                  {u.status === "pending" && (
                    <Badge className="ml-1 rounded-sm border border-amber-300 bg-amber-50 text-[10px] text-amber-800 hover:bg-amber-50" data-testid={`pending-badge-${u.email}`}>
                      Onay Bekliyor
                    </Badge>
                  )}
                </TableCell>
                <TableCell className="py-2 text-sm">
                  {u.organisation || u.supplier_name || "—"}
                  {u.status === "pending" && u.organisation_hint && (
                    <p className="text-[11px] text-stone-400">Talep: {u.organisation_hint}</p>
                  )}
                </TableCell>
                <TableCell className="py-2 text-xs text-stone-500">{fmtDate(u.created_at)}</TableCell>
                <TableCell className="py-2 text-right">
                  {u.status === "pending" && (
                    <>
                      <Button variant="ghost" size="sm" onClick={() => openEdit(u, true)} data-testid={`approve-user-${u.email}`} className="h-8 rounded-sm px-2 text-[#1C4E35] hover:bg-[#1C4E35]/10">
                        <CheckCircle2 size={14} className="mr-1" /> Onayla
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => remove(u)} data-testid={`reject-user-${u.email}`} className="h-8 rounded-sm px-2 text-red-600 hover:bg-red-50">
                        <XCircle size={14} className="mr-1" /> Reddet
                      </Button>
                    </>
                  )}
                  <Button variant="ghost" size="sm" onClick={() => openEdit(u)} data-testid={`edit-user-${u.email}`} className="h-8 rounded-sm px-2 hover:bg-stone-100">
                    <Pencil size={14} />
                  </Button>
                  {me?.email === "mbucak@gmail.com" && u.id !== me?.id && u.status !== "pending" && (
                    <>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openMenuDialog(u)}
                        title="Menü görünürlüğü"
                        data-testid={`menus-user-${u.email}`}
                        className="h-8 rounded-sm px-2 text-stone-500 hover:bg-stone-100"
                      >
                        <PanelLeft size={14} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => enterAccount(u)}
                        title={`${u.name || u.email} hesabına gir`}
                        data-testid={`impersonate-user-${u.email}`}
                        className="h-8 rounded-sm px-2 text-[#1C4E35] hover:bg-[#1C4E35]/10"
                      >
                        <LogIn size={14} />
                      </Button>
                    </>
                  )}
                  {u.id !== me?.id && u.status !== "pending" && (
                    <Button variant="ghost" size="sm" onClick={() => remove(u)} data-testid={`delete-user-${u.email}`} className="h-8 rounded-sm px-2 text-red-600 hover:bg-red-50">
                      <Trash2 size={14} />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
                ))}
              </Fragment>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent aria-describedby={undefined} className="rounded-sm bg-white sm:max-w-md" data-testid="user-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold tracking-tight">
              {approving ? "Kullanıcıyı Onayla" : editing ? "Kullanıcıyı Düzenle" : "Yeni Kullanıcı Oluştur"}
            </DialogTitle>
            {approving && editing?.organisation_hint && (
              <p className="text-xs text-stone-500">Kullanıcının talep ettiği firma: <strong>{editing.organisation_hint}</strong> — aşağıdan doğru firmayı seçip onaylayın.</p>
            )}
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-stone-600">Ad Soyad</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-form-name" className="rounded-sm border-stone-300" />
            </div>
            {!editing && (
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-stone-600">E-posta</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-form-email" className="rounded-sm border-stone-300" />
              </div>
            )}
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-stone-600">{editing ? "Yeni Şifre (boş bırakılabilir)" : "Şifre"}</Label>
              <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-form-password" className="rounded-sm border-stone-300" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-stone-600">Rol</Label>
              <RolePicker value={form.role} onChange={(v) => setForm({ ...form, role: v })} testId="user-form-role" />
            </div>
            {form.role === "supplier" && (
              <SupplierPicker suppliers={suppliers} value={form.supplier_id} onChange={(v) => setForm({ ...form, supplier_id: v })} testId="user-form-supplier" />
            )}
            {form.role === "user" && (
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-stone-600">Atanacak Firma</Label>
                <Select value={form.organisation} onValueChange={(v) => setForm({ ...form, organisation: v })}>
                  <SelectTrigger className="rounded-sm border-stone-300" data-testid="user-form-organisation">
                    <SelectValue placeholder="Firma seçin" />
                  </SelectTrigger>
                  <SelectContent className="max-h-64">
                    {orgs.map((o) => (
                      <SelectItem key={o} value={o}>
                        {o}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm border-stone-300">
              Vazgeç
            </Button>
            <Button onClick={save} disabled={saving} data-testid="user-form-submit" className="rounded-sm bg-[#1C4E35] text-white hover:bg-[#143A26] active:scale-95">
              {saving ? "Kaydediliyor..." : approving ? "Onayla ve Aktifleştir" : editing ? "Güncelle" : "Oluştur"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent aria-describedby={undefined} className="rounded-sm bg-white sm:max-w-md" data-testid="invite-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold tracking-tight">Kullanıcı Davet Et</DialogTitle>
            <p className="text-xs text-stone-500">Davet edilen kişiye 7 gün geçerli bir bağlantı gönderilir. Kişi adını ve şifresini belirleyerek hesabını hemen aktifleştirir.</p>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-stone-600">E-posta</Label>
              <Input type="email" value={inviteForm.email} onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })} data-testid="invite-form-email" className="rounded-sm border-stone-300" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-stone-600">Rol</Label>
              <RolePicker value={inviteForm.role} onChange={(v) => setInviteForm({ ...inviteForm, role: v })} testId="invite-form-role" />
            </div>
            {inviteForm.role === "supplier" && (
              <SupplierPicker suppliers={suppliers} value={inviteForm.supplier_id} onChange={(v) => setInviteForm({ ...inviteForm, supplier_id: v })} testId="invite-form-supplier" />
            )}
            {inviteForm.role === "user" && (
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-stone-600">Atanacak Firma</Label>
                <Select value={inviteForm.organisation} onValueChange={(v) => setInviteForm({ ...inviteForm, organisation: v })}>
                  <SelectTrigger className="rounded-sm border-stone-300" data-testid="invite-form-organisation">
                    <SelectValue placeholder="Firma seçin" />
                  </SelectTrigger>
                  <SelectContent className="max-h-64">
                    {orgs.map((o) => (
                      <SelectItem key={o} value={o}>
                        {o}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInviteOpen(false)} className="rounded-sm border-stone-300">
              Vazgeç
            </Button>
            <Button onClick={sendInvite} disabled={inviting} data-testid="invite-form-submit" className="rounded-sm bg-[#1C4E35] text-white hover:bg-[#143A26] active:scale-95">
              {inviting ? "Gönderiliyor..." : "Daveti Gönder"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!menuDialog} onOpenChange={(v) => !v && setMenuDialog(null)}>
        <DialogContent aria-describedby={undefined} className="max-w-sm rounded-sm bg-white" data-testid="menus-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-base font-bold tracking-tight">Menü Görünürlüğü</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-stone-500">{menuDialog?.name || menuDialog?.email} — işareti kaldırılan menüler bu kullanıcıda gizlenir. Rol kısıtları ayrıca geçerlidir.</p>
          <div className="space-y-1.5">
            {MENUS.map(([key, label]) => (
              <label key={key} className="flex cursor-pointer items-center gap-2 border border-stone-200 px-3 py-2 text-sm transition-colors hover:bg-stone-50" data-testid={`menu-toggle-${key}`}>
                <input
                  type="checkbox"
                  checked={!menuHidden.includes(key)}
                  onChange={(e) => setMenuHidden((h) => (e.target.checked ? h.filter((x) => x !== key) : [...h, key]))}
                  className="h-4 w-4 accent-[#1C4E35]"
                />
                <span className={menuHidden.includes(key) ? "text-stone-400 line-through" : "text-stone-800"}>{label}</span>
              </label>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMenuDialog(null)} className="rounded-sm border-stone-300">Vazgeç</Button>
            <Button onClick={saveMenus} data-testid="menus-save" className="rounded-sm bg-[#1C4E35] text-white hover:bg-[#143A26] active:scale-95">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
